import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Video, Clock, Calendar, Trash2, ExternalLink, GraduationCap } from "lucide-react";

const empty = { title: "", subject: "General", trainer_name: "", class_timing: "", meeting_url: "", start_date: "", notes: "" };

export default function StudentMyClasses() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const res = await api.get("/my-classes/me"); setItems(res.data); }
    catch { toast.error("Failed to load classes"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/my-classes", form);
      toast.success("Class added to your schedule");
      setForm(empty);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Remove this class?")) return;
    await api.delete(`/my-classes/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <Layout title="My Classes" subtitle="Your personal schedule">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-6 space-y-3" data-testid="add-class-form">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><Plus className="w-5 h-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Add class</div>
              <h2 className="font-display font-bold text-lg text-slate-900">External / self-study</h2>
            </div>
          </div>

          <Field label="Class name" testid="mc-title" required>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mc-title"
              placeholder="e.g. Advanced Chemistry Coaching"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <Field label="Subject" testid="mc-subject">
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="mc-subject"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <Field label="Trainer / Teacher">
            <input value={form.trainer_name} onChange={(e) => setForm({ ...form, trainer_name: e.target.value })} data-testid="mc-trainer"
              placeholder="Optional"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <Field label="Class timing" required>
            <input required value={form.class_timing} onChange={(e) => setForm({ ...form, class_timing: e.target.value })} data-testid="mc-timing"
              placeholder="e.g. Mon/Wed 7-8 PM IST"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <Field label="Room / Meeting link" required>
            <input required type="url" value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} data-testid="mc-meeting-url"
              placeholder="https://zoom.us/j/... or https://meet.google.com/..."
              className="w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <Field label="Start date">
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="mc-start"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="mc-notes"
              placeholder="Anything you want to remember"
              className="w-full min-h-[70px] px-3 py-2 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm" />
          </Field>
          <button disabled={saving} data-testid="mc-add-btn" className="w-full py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Adding…" : "Add to my schedule"}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-4" data-testid="my-classes-list">
          {items.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
              <GraduationCap className="w-8 h-8 mx-auto text-slate-300" />
              <div className="mt-2 font-display font-bold text-slate-900">No classes yet</div>
              <p className="text-sm text-slate-500 mt-1">Add your first external class using the form on the left.</p>
            </div>
          )}
          {items.map((c) => (
            <div key={c.id} data-testid={`mc-${c.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 stagger-in">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{c.subject}</div>
                  <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{c.title}</h3>
                  {c.trainer_name && <div className="text-xs text-slate-500 mt-0.5">Trainer: {c.trainer_name}</div>}
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-blue-600" /> {c.class_timing}</span>
                    {c.start_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-emerald-600" /> Starts {c.start_date}</span>}
                  </div>
                  {c.notes && <p className="text-xs text-slate-500 mt-2 line-clamp-3">{c.notes}</p>}
                </div>
                <button onClick={() => del(c.id)} data-testid={`mc-del-${c.id}`} className="text-slate-400 hover:text-red-500 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <a href={c.meeting_url} target="_blank" rel="noreferrer" data-testid={`mc-join-${c.id}`}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm hover:bg-emerald-700 transition-colors">
                <Video className="w-4 h-4" /> Join class <ExternalLink className="w-3 h-3 opacity-70" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
